import { useState } from "react"
import { Button } from "~/react/ui/button"
import { Separator } from "~/react/ui/separator"
import { Popover, PopoverTrigger, PopoverContent } from "~/react/ui/popover"
import { Input } from "~/react/ui/input"
import { Slider } from "~/react/ui/slider"
import { t } from "../i18n"
import { cn } from "~/lib/utils"

interface ConfigureDistributionPopoverProps {
  children: React.ReactNode
}

const DISTRIBUTION_MIN = 20
const DISTRIBUTION_MAX = 80
const DISTRIBUTION_STEP = 10

function clampToInt(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value.replace(/[^\d]/g, ""), 10)
  if (Number.isNaN(parsed)) return min
  return Math.max(min, Math.min(max, parsed))
}

function snapToStep(value: number): number {
  const clamped = Math.max(DISTRIBUTION_MIN, Math.min(DISTRIBUTION_MAX, value))
  return (
    Math.round(clamped / DISTRIBUTION_STEP) * DISTRIBUTION_STEP
  )
}

function ConfigureDistributionControls() {
  const [percentA, setPercentA] = useState(50)

  const percentB = 100 - percentA

  return (
    <div className="variant-distribution-popover__content">
      <div className="variant-distribution-popover__title">
        {t("js.campaigns.content.ab_distribution_configure_title")}
      </div>

      <div className="variant-distribution-popover__labels-row">
        <span className="variant-distribution-popover__variant-label">
          {t("js.campaigns.content.ab_variant_a")}
        </span>
        <span className="variant-distribution-popover__variant-label">
          {t("js.campaigns.content.ab_variant_b")}
        </span>
      </div>

      <Slider
        size="sm"
        className="variant-distribution-popover__slider"
        value={[percentA]}
        min={DISTRIBUTION_MIN}
        max={DISTRIBUTION_MAX}
        step={DISTRIBUTION_STEP}
        showMarks
        onValueChange={(values) => {
          const next = snapToStep(values[0] ?? 50)
          setPercentA(next)
        }}
      />

      <div className="variant-distribution-popover__inputs-row">
        <Input
          size="sm"
          inputMode="numeric"
          className="variant-distribution-popover__percent-input"
          value={`${percentA}%`}
          onChange={(e) => {
            const next = snapToStep(
              clampToInt(e.target.value, DISTRIBUTION_MIN, DISTRIBUTION_MAX),
            )
            setPercentA(next)
          }}
        />

        <Input
          size="sm"
          inputMode="numeric"
          className="variant-distribution-popover__percent-input"
          value={`${percentB}%`}
          onChange={(e) => {
            const nextB = snapToStep(
              clampToInt(e.target.value, DISTRIBUTION_MIN, DISTRIBUTION_MAX),
            )
            const nextA = 100 - nextB
            setPercentA(nextA)
          }}
        />
      </div>
    </div>
  )
}

function ConfigureDistributionPopover({
  children,
}: ConfigureDistributionPopoverProps) {
  const [open, setOpen] = useState(false)

  const handleSave = () => {
    // TODO(dev, CAMPAIGNS_AB_TESTING): Implement save distribution logic
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="variant-distribution-popover"
        style={{ right: 0, left: "auto" }}
        side="bottom"
        align="end"
        sideOffset={4}
        collisionPadding={8}
      >
        {/* TODO(dev, CAMPAIGNS_AB_TESTING): Wire this to real state + saving */}
        <ConfigureDistributionControls />

        <Button
          type="button"
          variant="secondary"
          size="sm"
          fullWidth
          className="variant-distribution-popover__save"
          onClick={handleSave}
        >
          {t("js.campaigns.content.ab_distribution_save")}
        </Button>
      </PopoverContent>
    </Popover>
  )
}

export function Distribution() {
  return (
    <div className={cn("follow-up-editor__distribution-content")}>
      <dl className="compose-message-metas">
        <div className="compose-message-meta">
          <dt className="compose-message-meta__label">
            {t("js.campaigns.content.ab_distribution_label")}
          </dt>
          <dd className="compose-message-meta__value">50% – 50%</dd>
        </div>
      </dl>
      <Separator orientation="vertical" className="follow-up-editor__distribution-divider" />
      <ConfigureDistributionPopover>
        <Button type="button" variant="linkGrey" size="sm">
          {t("js.campaigns.content.ab_distribution_configure")}
        </Button>
      </ConfigureDistributionPopover>
    </div>
  )
}
